// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ClipboardList, Plus, Search } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Badge, EmptyState, Select, Pagination, DataTable, Skeleton, Input } from '@/components/ui'
import { MobileSortControl } from '@/components/ui/MobileSortControl'
import type { Column } from '@/components/ui/DataTable'
import { useWorkOrders, type WorkOrderFilters } from '@/hooks/useCmms'
import { useEquipment } from '@/hooks/useCmms'
import { useUsers } from '@/hooks/useUsers'
import { useListSort } from '@/hooks/useListSort'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import type { WorkOrder } from '@/types'

export function WorkOrderListPage() {
  const { t } = useTranslation('cmms')
  const { t: tc } = useTranslation('common')
  const navigate = useNavigate()
  const formatDate = useDateFormatter()
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState<WorkOrderFilters>({})
  const [searchQuery, setSearchQuery] = useState('')
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)

  // EP-97: default-rule page — updated_at desc, user-selectable server-side sort.
  const { sort, order, setSort, toggleOrder, sortParams } = useListSort({
    defaultSort: 'updated_at',
    defaultOrder: 'desc',
  })
  const handleSort = (key: string) => { setPage(1); setSort(key) }
  const handleToggleOrder = () => { setPage(1); toggleOrder() }

  const { data, isLoading, isFetching, error } = useWorkOrders(page, { ...filters, q: searchQuery || undefined, ...sortParams })
  const { data: equipmentData } = useEquipment(1, {})
  const { data: usersData } = useUsers()

  const workOrders = data?.data || []
  const allEquipment = equipmentData?.data || []
  const users = usersData?.data || []

  const statusOptions = [
    { value: '', label: t('work_order_status.all') },
    ...(['open', 'in_progress', 'completed', 'cancelled'] as const).map(
      (s) => ({ value: s, label: t(`work_order_status.${s}`) })
    ),
  ]

  const priorityOptions = [
    { value: '', label: t('priorities.all') },
    ...(['low', 'medium', 'high', 'urgent'] as const).map(
      (p) => ({ value: p, label: t(`priorities.${p}`) })
    ),
  ]

  const equipmentOptions = [
    { value: '', label: t('filters.all_equipment') },
    ...allEquipment.map((e) => ({ value: String(e.id), label: e.name })),
  ]

  const assigneeOptions = [
    { value: '', label: t('filters.all_assignees') },
    ...users.map((u) => ({ value: String(u.id), label: u.full_name })),
  ]

  const getStatusBadge = (wo: WorkOrder) => {
    const variants: Record<WorkOrder['status'], 'warning' | 'info' | 'success' | 'ghost'> = {
      open: 'warning',
      in_progress: 'info',
      completed: 'success',
      cancelled: 'ghost',
    }
    return <Badge variant={variants[wo.status]}>{t(`work_order_status.${wo.status}`)}</Badge>
  }

  const getPriorityBadge = (wo: WorkOrder) => {
    const variants: Record<WorkOrder['priority'], 'ghost' | 'warning' | 'error' | 'error'> = {
      low: 'ghost',
      medium: 'warning',
      high: 'error',
      urgent: 'error',
    }
    return <Badge variant={variants[wo.priority]}>{t(`priorities.${wo.priority}`)}</Badge>
  }

  const columns: Column<WorkOrder>[] = [
    { key: 'title', header: t('labels.title'), render: (wo) => <span className="font-medium">{wo.title}</span> },
    { key: 'priority', header: t('labels.priority'), sortable: true, render: (wo) => getPriorityBadge(wo) },
    { key: 'status', header: t('labels.status'), sortable: true, render: (wo) => getStatusBadge(wo) },
    { key: 'equipment', header: t('labels.equipment'), render: (wo) => wo.equipment?.name || '-', hideBelow: 'md' },
    { key: 'assigned_to', header: t('labels.assigned_to'), render: (wo) => wo.assigned_to?.name || wo.vendor?.name || '-', hideBelow: 'md' },
    {
      key: 'due_date',
      header: t('labels.due_date'),
      sortable: true,
      render: (wo) => wo.due_date ? formatDate(wo.due_date) : '-',
    },
  ]

  // EP-97: mobile sort options mirror the allow-listed sortable columns.
  const sortOptions = [
    { value: 'updated_at', label: tc('sort.by_last_updated') },
    { value: 'created_at', label: tc('sort.by_newest') },
    { value: 'due_date', label: t('labels.due_date') },
    { value: 'priority', label: t('labels.priority') },
    { value: 'status', label: t('labels.status') },
  ]

  const activeFilterCount = [filters.status || '', filters.priority || '', filters.equipment_id ? '1' : '', filters.assigned_to_id ? '1' : ''].filter(Boolean).length

  return (
    <div className="space-y-6">
      <Breadcrumbs items={[{ label: t('work_orders.title') }]} />

      <PageHeader
        title={t('work_orders.title')}
        description={t('work_orders.description')}
        isRefreshing={isFetching && !isLoading}
        actions={
          <Button size="sm" leftIcon={<Plus className="w-4 h-4" />} onClick={() => navigate('/work-orders/new')}>
            {t('work_orders.new')}
          </Button>
        }
      />

      <Card>
        {/* Search — always visible */}
        <div className="w-full min-w-0">
          <Input
            label={tc('actions.search')}
            leftIcon={<Search className="w-4 h-4" />}
            placeholder={t('filters.search_work_orders')}
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(1) }}
          />
        </div>

        {/* Mobile-only toggle */}
        <button
          type="button"
          className="sm:hidden flex items-center justify-between w-full cursor-pointer select-none font-medium mt-3"
          aria-expanded={mobileFiltersOpen}
          aria-controls="workorder-filters-body"
          onClick={() => setMobileFiltersOpen((v) => !v)}
        >
          <span>
            {tc('filters.toggle')}
            {activeFilterCount > 0 && (
              <Badge variant="count" size="sm" className="ml-2">{activeFilterCount}</Badge>
            )}
          </span>
          <span className={`text-base-content/60 transition-transform duration-200 ${mobileFiltersOpen ? 'rotate-180' : ''}`}>▾</span>
        </button>

        <div
          id="workorder-filters-body"
          className={`flex-wrap gap-4 items-end mt-3 ${mobileFiltersOpen ? 'flex' : 'hidden sm:flex'}`}
        >
          {/* EP-97: mobile sort control — desktop sorts via clickable table headers. */}
          <div className="w-full sm:hidden">
            <MobileSortControl
              options={sortOptions}
              value={sort}
              order={order}
              onChange={handleSort}
              onToggleOrder={handleToggleOrder}
            />
          </div>
          <Select
            label={t('labels.status')}
            value={filters.status || ''}
            onChange={(e) => {
              setFilters((f) => ({ ...f, status: e.target.value || undefined }))
              setPage(1)
            }}
            options={statusOptions}
            className="w-full sm:w-44 min-w-0"
          />
          <Select
            label={t('labels.priority')}
            value={filters.priority || ''}
            onChange={(e) => {
              setFilters((f) => ({ ...f, priority: e.target.value || undefined }))
              setPage(1)
            }}
            options={priorityOptions}
            className="w-full sm:w-44 min-w-0"
          />
          <Select
            label={t('labels.equipment')}
            value={filters.equipment_id ? String(filters.equipment_id) : ''}
            onChange={(e) => {
              setFilters((f) => ({ ...f, equipment_id: e.target.value ? Number(e.target.value) : undefined }))
              setPage(1)
            }}
            options={equipmentOptions}
            className="w-full sm:w-48 min-w-0"
          />
          <Select
            label={t('labels.assigned_to')}
            value={filters.assigned_to_id ? String(filters.assigned_to_id) : ''}
            onChange={(e) => {
              setFilters((f) => ({ ...f, assigned_to_id: e.target.value ? Number(e.target.value) : undefined }))
              setPage(1)
            }}
            options={assigneeOptions}
            className="w-full sm:w-48 min-w-0"
          />
        </div>
      </Card>

      {isLoading ? (
        <Card>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </div>
        </Card>
      ) : error ? (
        <Card>
          <EmptyState
            title={t('messages.error_loading')}
            description={t('messages.error_description')}
            action={<Button onClick={() => window.location.reload()}>{tc('actions.retry')}</Button>}
          />
        </Card>
      ) : !workOrders.length ? (
        <Card>
          <EmptyState
            icon={<ClipboardList className="w-12 h-12" />}
            title={t('empty.work_orders')}
            action={
              <Button leftIcon={<Plus className="w-4 h-4" />} onClick={() => navigate('/work-orders/new')}>
                {t('work_orders.new')}
              </Button>
            }
          />
        </Card>
      ) : (
        <Card>
          <DataTable
            columns={columns}
            data={workOrders}
            keyExtractor={(wo) => wo.id}
            onRowClick={(wo) => navigate(`/work-orders/${wo.id}`)}
            sortBy={sort}
            sortOrder={order}
            onSort={handleSort}
          />
        </Card>
      )}

      {data?.meta && data.meta.total_count > 0 && (
        <div className="flex justify-center">
          <Pagination
            currentPage={data.meta.current_page}
            totalPages={data.meta.total_pages}
            totalItems={data.meta.total_count}
            pageSize={data.meta.per_page}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  )
}
